import DOMPurify from 'dompurify';

// Source text reaching us can already carry HTML entities: the one-pager generation
// pipeline returns plain-text explanations containing "P&amp;L", and Salesforce long-text
// fields do the same. Escaping those a second time produced "&amp;amp;", which the parser
// then decodes one level, leaving a literal "&amp;" on the page and in the PDF. Escaping
// only bare ampersands makes this idempotent. "<" and ">" are still escaped
// unconditionally, so no entity can be smuggled through into a tag.
const BARE_AMPERSAND = /&(?!(?:[a-zA-Z][a-zA-Z0-9]{1,31}|#\d{1,7}|#[xX][0-9a-fA-F]{1,6});)/g;

export const escapeHtml = (text: string) =>
  text
    .replace(BARE_AMPERSAND, '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

export const htmlToPlainText = (html: string): string => {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent ?? '').trim();
  } catch {
    return html;
  }
};

// Stored one-pager content sometimes arrives with entity-escaped HTML — either the whole
// string ("&lt;p&gt;...") or escaped tags nested inside real ones ("<p>...&lt;em&gt;x&lt;/em&gt;</p>"),
// an artifact of the generation pipeline (BPP-226). Decode it so editors and display get
// real HTML. Clean Quill output has no escaped tags, so this is a no-op on round-trips.
// Mirrors normalizeHtml in the PDF renderers (OnePagerPdf.tsx / pdfRichText.tsx) so the
// web view and the PDF interpret stored content identically.
export const decodeEscapedHtml = (raw: string): string => {
  const value = raw ?? '';
  if (!value.includes('&lt;') && !value.includes('&gt;')) return value;
  return value
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/&amp;/g, '&');
};

export const sanitizeHtml = (html: string): string => {
  try {
    return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
  } catch {
    return '';
  }
};

// Plain-text rendering for strings that may arrive as HTML (and/or entity-escaped),
// e.g. MLE warning messages now wrapped in <p>…</p>. Tags become spaces so adjacent
// blocks don't run together. Display-only: warning dismissal keys hash the RAW
// message on both frontend and backend, so never feed this through those paths.
export const stripHtmlToText = (raw: string): string =>
  decodeEscapedHtml(raw ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const snapshotHtmlToAlignmentPoints = (html: string): string[] => {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const lis = Array.from(doc.querySelectorAll('li'));
    const points = lis
      .map((li) => (li.textContent ?? '').trim())
      .filter((t) => !!t);
    if (points.length > 0) return points;

    const fallback = (doc.body.textContent ?? '').trim();
    return fallback ? [fallback] : [];
  } catch {
    return [];
  }
};

const isListLine = (line: string) => /^([-*•‣]|\d+[.)])\s+/.test(line);

// Rejoins lines that a fixed-column hard wrap split mid-sentence, leaving only the breaks
// that carry meaning (list items).
const unwrapHardBreaks = (block: string): string[] =>
  block
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => !!line)
    .reduce<string[]>((lines, line) => {
      const prev = lines[lines.length - 1];
      if (prev === undefined || isListLine(line)) {
        lines.push(line);
        return lines;
      }
      // A word split across the wrap ("improve-\nment") rejoins with no space. The hyphen is
      // deliberately kept: a line ending in one is ambiguous between a split word and a real
      // compound ("high-speed"), and dropping it would corrupt the latter in a client-facing
      // document.
      lines[lines.length - 1] = /[A-Za-z]-$/.test(prev) ? prev + line : `${prev} ${line}`;
      return lines;
    }, []);

// Salesforce's client project description is a plain-text long-text field that arrives hard
// wrapped at a fixed column. Emitting one <br /> per newline reproduced that narrow column
// instead of letting the paragraph reflow to its container — and dropping the tag alone
// wouldn't be enough, since .ql-editor sets `white-space: pre-wrap` and renders a raw newline
// as a break regardless. Blank lines still separate paragraphs.
// Shared by the web card (ProjectOverview) and the PDF (App's projectOverviewPdfHtml) so both
// surfaces render the same paragraphs from the same source text.
export const plainTextToHtml = (raw: string): string | undefined => {
  const value = raw?.trim();
  if (!value) return undefined;

  const blocks = value
    .replace(/<br\s*\/?>/gi, '\n')
    .split(/\r?\n\s*\r?\n/)
    .map((b) => b.trim())
    .filter((b) => !!b);

  if (blocks.length === 0) return undefined;

  return blocks
    .map((b) => `<p>${unwrapHardBreaks(b).map(escapeHtml).join('<br />')}</p>`)
    .join('');
};
